const STORAGE_KEY = 'tsg:guest-cart-token';

/**
 * Stores the signed-out visitor's guest cart token for the lifetime of
 * this browser tab -- `sessionStorage`, not `localStorage`, the same
 * "ephemeral but survives a reload/retry, not a browser restart" choice
 * `lib/idempotency.ts` already makes for checkout idempotency keys. A
 * guest cart shouldn't silently persist across browser restarts (that
 * would let two different people sharing a machine see each other's
 * "abandoned" guest cart), so this deliberately does NOT use
 * `localStorage`.
 *
 * The token itself is always server-generated (`POST /cart/guest-session`)
 * -- nothing here ever mints or guesses one.
 */
export function getStoredGuestCartToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // sessionStorage unavailable (private browsing, disabled storage) --
    // fail to "no guest session", which just means the next mutation
    // creates a fresh one.
    return null;
  }
}

export function storeGuestCartToken(token: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Best-effort only.
  }
}

/** Cleared after a successful sign-in merge attempt, and always regardless of merge success/failure -- a guest token is single-use for merging (the backend converts it), so retrying with the same token would only ever 404. */
export function clearGuestCartToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort only.
  }
}
